import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import {
  type ClipCanvasContextValue,
  type ClipGeneratorNodeId,
  type ReferenceInjectEvent,
} from './types';

const ClipCanvasContext = createContext<ClipCanvasContextValue | null>(null);

type ClipCanvasProviderProps = {
  children: ReactNode;
} & Omit<ClipCanvasContextValue, 'injectEvents' | 'requestInjectReference'>;

export function ClipCanvasProvider({
  children,
  videoClip,
  episode,
  models,
  modelsLoading,
  editConfig,
  onEditConfigChange,
  refreshVideoClip,
  resources,
  isPolling,
  onTriggerResourcePoll,
}: ClipCanvasProviderProps) {
  const [injectEvents, setInjectEvents] = useState<
    Record<string, ReferenceInjectEvent | undefined>
  >({});

  // 单调递增的 nonce，保证每次注入都触发生成器表单的一次性 effect
  const nonceRef = useRef(0);

  const requestInjectReference = useCallback((target: ClipGeneratorNodeId, url: string, mediaType: 'image' | 'audio' = 'image') => {
    if (!url) return;
    nonceRef.current += 1;
    const nonce = nonceRef.current;
    setInjectEvents((prev) => ({ ...prev, [target]: { url, nonce, mediaType } }));
  }, []);

  // 注意：必须按字段依赖，不能用 `{...shared}` rest 展开。
  // rest 展开每次渲染都会生成新对象，使 useMemo 失效，导致 context value 每次都变，
  // 进而让所有画布节点/表单在每次父组件渲染时都重渲染（打开画布时会显著加重主线程负担）。
  const value = useMemo<ClipCanvasContextValue>(
    () => ({
      videoClip,
      episode,
      models,
      modelsLoading,
      editConfig,
      onEditConfigChange,
      refreshVideoClip,
      resources,
      isPolling,
      onTriggerResourcePoll,
      injectEvents,
      requestInjectReference,
    }),
    [
      videoClip,
      episode,
      models,
      modelsLoading,
      editConfig,
      onEditConfigChange,
      refreshVideoClip,
      resources,
      isPolling,
      onTriggerResourcePoll,
      injectEvents,
      requestInjectReference,
    ],
  );

  return <ClipCanvasContext.Provider value={value}>{children}</ClipCanvasContext.Provider>;
}

export function useClipCanvasContext(): ClipCanvasContextValue {
  const ctx = useContext(ClipCanvasContext);
  if (!ctx) {
    throw new Error('useClipCanvasContext must be used within a ClipCanvasProvider');
  }
  return ctx;
}
